<template>
  <div class="dynamic-field-picker">
    <!-- Módulo selector -->
    <div class="mb-3">
      <label class="block text-xs font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider mb-1.5">
        Módulo
      </label>
      <div class="flex gap-2">
        <button
          v-for="mod in availableModules"
          :key="mod.id"
          @click="selectedModule = mod.id"
          class="flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors"
          :class="selectedModule === mod.id
            ? 'bg-primary-600 text-white border-primary-600 dark:bg-primary-500'
            : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700'"
        >
          {{ mod.label }}
        </button>
      </div>
    </div>

    <!-- Modo: Campo individual vs Tabla -->
    <div class="mb-3">
      <div class="flex gap-2">
        <button
          @mousedown.prevent
          @click="pickerMode = 'field'"
          class="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
          :class="pickerMode === 'field'
            ? 'bg-blue-600 text-white border-blue-600'
            : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50'"
        >
          Campo individual
        </button>
        <button
          @mousedown.prevent
          @click="pickerMode = 'table'"
          class="flex-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
          :class="pickerMode === 'table'
            ? 'bg-emerald-600 text-white border-emerald-600'
            : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50'"
        >
          Tabla
        </button>
      </div>
    </div>

    <!-- Búsqueda -->
    <div class="mb-3">
      <div class="relative">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-400" />
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Buscar campo..."
          class="w-full pl-9 pr-3 py-2 text-sm border border-secondary-200 dark:border-secondary-700 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>
    </div>

    <!-- Presets de tablas (solo para embarques en modo tabla, sin source activo) -->
    <div v-if="pickerMode === 'table' && selectedModule === 'shipments' && !activeTableSource" class="mb-3 space-y-1.5">
      <span class="text-xs font-semibold text-secondary-400 dark:text-secondary-500 uppercase tracking-wider px-1">Tablas predefinidas</span>
      <button
        @mousedown.prevent
        @click="insertQuotationPreset('quotationContainers')"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors text-left"
      >
        <Package class="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <span class="text-emerald-800 dark:text-emerald-200 font-medium block text-xs">Contenedores de Cotización</span>
          <span class="text-emerald-500 dark:text-emerald-500 text-[10px]">Tipo · Bultos · Peso Bruto · Volumen</span>
        </div>
      </button>
      <button
        @mousedown.prevent
        @click="insertQuotationPreset('quotationCharges')"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors text-left"
      >
        <Receipt class="w-4 h-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <span class="text-emerald-800 dark:text-emerald-200 font-medium block text-xs">Cargos de Cotización</span>
          <span class="text-emerald-500 dark:text-emerald-500 text-[10px]">Concepto · Categoría · Valor Unit. · IVA · Total · Moneda</span>
        </div>
      </button>
      <div class="border-b border-secondary-200 dark:border-secondary-700 pt-1 mt-1"></div>
      <span class="text-xs font-semibold text-secondary-400 dark:text-secondary-500 uppercase tracking-wider px-1">Tablas personalizables</span>
      <button
        @mousedown.prevent
        @click="selectPreset('containers')"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors text-left"
      >
        <Container class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <span class="text-blue-800 dark:text-blue-200 font-medium block text-xs">Contenedores</span>
          <span class="text-blue-500 dark:text-blue-400 text-[10px]">Personaliza columnas desde campos del sistema</span>
        </div>
      </button>
      <button
        @mousedown.prevent
        @click="selectPreset('items')"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors text-left"
      >
        <ShoppingBag class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <span class="text-blue-800 dark:text-blue-200 font-medium block text-xs">Productos / Items</span>
          <span class="text-blue-500 dark:text-blue-400 text-[10px]">Personaliza columnas desde campos del sistema</span>
        </div>
      </button>
      <button
        @mousedown.prevent
        @click="selectPreset('charges')"
        class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors text-left"
      >
        <CircleDollarSign class="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <div class="flex-1 min-w-0">
          <span class="text-blue-800 dark:text-blue-200 font-medium block text-xs">Cargos</span>
          <span class="text-blue-500 dark:text-blue-400 text-[10px]">Personaliza columnas desde campos del sistema</span>
        </div>
      </button>
      <div class="border-b border-secondary-200 dark:border-secondary-700 pt-1"></div>
      <span class="text-xs font-semibold text-secondary-400 dark:text-secondary-500 uppercase tracking-wider px-1 block pt-1">Tabla personalizada</span>
    </div>

    <!-- Encabezado de source activo (personalización de columnas) -->
    <div v-if="activeTableSource" class="mb-3">
      <button
        @mousedown.prevent
        @click="clearActiveSource"
        class="flex items-center gap-1.5 text-xs text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300 mb-2"
      >
        <ChevronLeft class="w-3.5 h-3.5" />
        Volver a presets
      </button>
      <div class="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <span class="text-blue-800 dark:text-blue-200 font-semibold text-xs block">{{ SOURCE_LABELS[activeTableSource] || activeTableSource }}</span>
        <span class="text-blue-500 dark:text-blue-400 text-[10px]">Selecciona las columnas que deseas incluir</span>
      </div>
    </div>

    <!-- Modo tabla: columnas seleccionadas + botón insertar -->
    <div v-if="pickerMode === 'table' && selectedTableColumns.length > 0" class="mb-3 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
      <div class="flex items-center justify-between mb-1.5">
        <span class="text-xs font-semibold text-emerald-700 dark:text-emerald-300">{{ selectedTableColumns.length }} columna(s)</span>
        <button
          @mousedown.prevent
          @click="selectedTableColumns = []"
          class="text-xs text-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-300"
        >Limpiar</button>
      </div>
      <div class="flex flex-wrap gap-1 mb-2">
        <span
          v-for="col in selectedTableColumns"
          :key="col.fieldId"
          class="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-800/40 text-emerald-700 dark:text-emerald-300 rounded-full text-xs"
        >
          {{ col.fieldLabel }}
          <button @mousedown.prevent @click="removeTableColumn(col.fieldId)" class="hover:text-red-500">&times;</button>
        </span>
      </div>
      <button
        @mousedown.prevent
        @click="emitInsertTable"
        class="w-full px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
      >
        Insertar tabla
      </button>
    </div>

    <!-- Campos de sub-colección (cuando hay source activo) -->
    <div v-if="activeTableSource" class="max-h-64 overflow-y-auto space-y-1 pr-1">
      <div v-if="activeSourceFields.length === 0" class="text-center py-8 text-sm text-secondary-400">
        No se encontraron campos para esta sección
      </div>
      <label
        v-for="col in activeSourceFields"
        :key="col.fieldId"
        @mousedown.prevent
        class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors cursor-pointer text-left"
      >
        <input
          type="checkbox"
          :checked="isSourceColumnSelected(col)"
          @change="toggleSourceColumn(col)"
          class="w-4 h-4 rounded border-secondary-300 text-emerald-600 focus:ring-emerald-500"
        />
        <div class="flex-1 min-w-0">
          <span class="text-secondary-900 dark:text-white font-medium block truncate">
            {{ col.fieldLabel }}
          </span>
          <span class="text-xs text-secondary-400 dark:text-secondary-500">
            {{ col.fieldId }} · {{ col.fieldType }}
          </span>
        </div>
      </label>
    </div>

    <!-- Lista de campos agrupados por sección (modo normal, sin source activo) -->
    <div v-if="!activeTableSource" class="max-h-64 overflow-y-auto space-y-3 pr-1">
      <div v-if="isLoadingFields" class="flex items-center justify-center py-8">
        <div class="animate-spin w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full"></div>
        <span class="ml-2 text-sm text-secondary-500">Cargando campos...</span>
      </div>

      <div v-else-if="groupedFields.length === 0" class="text-center py-8 text-sm text-secondary-400">
        No se encontraron campos
      </div>

      <div v-for="group in groupedFields" :key="group.section" class="space-y-1">
        <h4 class="text-xs font-semibold text-secondary-400 dark:text-secondary-500 uppercase tracking-wider px-1 sticky top-0 bg-white dark:bg-secondary-800 py-1">
          {{ formatSectionLabel(group.section) }}
        </h4>

        <!-- Modo campo: click inserta directamente -->
        <template v-if="pickerMode === 'field'">
          <button
            v-for="field in group.fields"
            :key="field.id"
            @mousedown.prevent
            @click="$emit('select', buildDynamicField(field))"
            class="w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors group text-left"
          >
            <div class="flex-1 min-w-0">
              <span class="text-secondary-900 dark:text-white font-medium block truncate">
                {{ field.label || field.name }}
              </span>
              <span class="text-xs text-secondary-400 dark:text-secondary-500">
                {{ field.name }} · {{ field.type }}
              </span>
            </div>
            <Plus class="w-4 h-4 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-2" />
          </button>
        </template>

        <!-- Modo tabla: checkboxes para seleccionar columnas -->
        <template v-else>
          <label
            v-for="field in group.fields"
            :key="field.id"
            @mousedown.prevent
            class="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors cursor-pointer text-left"
          >
            <input
              type="checkbox"
              :checked="isColumnSelected(field)"
              @change="toggleTableColumn(field)"
              class="w-4 h-4 rounded border-secondary-300 text-emerald-600 focus:ring-emerald-500"
            />
            <div class="flex-1 min-w-0">
              <span class="text-secondary-900 dark:text-white font-medium block truncate">
                {{ field.label || field.name }}
              </span>
              <span class="text-xs text-secondary-400 dark:text-secondary-500">
                {{ field.name }} · {{ field.type }}
              </span>
            </div>
          </label>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { Search, Plus, Package, Receipt, Container, ShoppingBag, CircleDollarSign, ChevronLeft } from 'lucide-vue-next' // eslint-disable-line
import { useSystemFieldsConfig } from '@/composables/useSystemFieldsConfig'
import { useCompanyStore } from '@/stores/company'
import type { FieldDefinition } from '@/interfaces/fields-management'
import type { TemplateDynamicField, TemplateModuleSource, TemplateDynamicTableColumn } from '@/types/emailTemplate'

const emit = defineEmits<{
  (e: 'select', field: TemplateDynamicField): void
  (e: 'insert-table', payload: { module: TemplateModuleSource; columns: TemplateDynamicTableColumn[]; source?: string }): void
}>()

const props = defineProps<{
  initialModule?: TemplateModuleSource
}>()

const { getModuleFields, loadSystemFieldsConfig, isLoading: isLoadingFields } = useSystemFieldsConfig()
const companyStore = useCompanyStore()

const availableModules = [
  { id: 'shipments' as TemplateModuleSource, label: 'Embarques' },
  { id: 'purchaseOrders' as TemplateModuleSource, label: 'Órdenes de Compra' },
]

const selectedModule = ref<TemplateModuleSource>(props.initialModule || 'shipments')
const searchQuery = ref('')
const pickerMode = ref<'field' | 'table'>('field')
const selectedTableColumns = ref<TemplateDynamicTableColumn[]>([])
const activeTableSource = ref<string | null>(null)

const SOURCE_LABELS: Record<string, string> = {
  containers: 'Contenedores',
  items: 'Productos / Items',
  charges: 'Cargos',
}

const PRESET_DEFAULTS: Record<string, string[]> = {
  containers: ['number', 'type', 'containerStatus', 'vgm', 'volume', 'loadingDate'],
  items: ['sku', 'description', 'quantity', 'invoiceNumber', 'unitValue', 'currency', 'totalValue'],
  charges: ['concept', 'supplier', 'chargeCategory', 'currency', 'value', 'invoiceNumber'],
}

const CHARGE_FIELDS: TemplateDynamicTableColumn[] = [
  { fieldId: 'concept', fieldLabel: 'Concepto', fieldType: 'text' },
  { fieldId: 'supplier', fieldLabel: 'Proveedor', fieldType: 'text' },
  { fieldId: 'chargeCategory', fieldLabel: 'Categoría', fieldType: 'text' },
  { fieldId: 'currency', fieldLabel: 'Moneda', fieldType: 'text' },
  { fieldId: 'value', fieldLabel: 'Valor', fieldType: 'currency' },
  { fieldId: 'invoiceNumber', fieldLabel: 'No. Factura', fieldType: 'text' },
  { fieldId: 'invoiceDate', fieldLabel: 'Fecha Factura', fieldType: 'date' },
  { fieldId: 'originalConcept', fieldLabel: 'Concepto Original', fieldType: 'text' },
  { fieldId: 'valorDcto', fieldLabel: 'Valor Documento', fieldType: 'currency' },
  { fieldId: 'tc', fieldLabel: 'Tipo de Cambio', fieldType: 'number' },
  { fieldId: 'valorMonedaLocal', fieldLabel: 'Valor Moneda Local', fieldType: 'currency' },
]

function mapContainerFieldId(name: string): string {
  if (name.startsWith('container')) {
    const rest = name.slice('container'.length)
    if (rest) return rest.charAt(0).toLowerCase() + rest.slice(1)
  }
  return name
}

const activeSourceFields = computed<TemplateDynamicTableColumn[]>(() => {
  const source = activeTableSource.value
  if (!source) return []

  if (source === 'charges') return CHARGE_FIELDS

  const allFields = getModuleFields.value('shipments') || []
  let filtered: FieldDefinition[] = []

  if (source === 'containers') {
    filtered = allFields.filter(f => f.containerField === true)
  } else if (source === 'items') {
    filtered = allFields.filter(f => f.section === 'items')
  }

  const columns = filtered
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(f => {
      const rawName = f.name || f.id
      const fieldId = source === 'containers' ? mapContainerFieldId(rawName) : rawName
      return {
        fieldId,
        fieldLabel: f.label || rawName,
        fieldType: f.type as string || 'text',
      }
    })

  // El estado del contenedor vive en el catálogo de la compañía, no en el banco
  // de campos: se ofrece siempre como columna disponible.
  if (source === 'containers' && !columns.some(c => c.fieldId === 'containerStatus')) {
    columns.push({ fieldId: 'containerStatus', fieldLabel: 'Estado del Contenedor', fieldType: 'text' })
  }

  return columns
})

function isSourceColumnSelected(col: TemplateDynamicTableColumn): boolean {
  return selectedTableColumns.value.some(c => c.fieldId === col.fieldId)
}

function toggleSourceColumn(col: TemplateDynamicTableColumn) {
  const idx = selectedTableColumns.value.findIndex(c => c.fieldId === col.fieldId)
  if (idx >= 0) {
    selectedTableColumns.value.splice(idx, 1)
  } else {
    selectedTableColumns.value.push({ ...col })
  }
}

function selectPreset(source: string) {
  activeTableSource.value = source
  pickerMode.value = 'table'
  const defaults = PRESET_DEFAULTS[source] || []
  const allCols = activeSourceFields.value
  selectedTableColumns.value = allCols.filter(c => defaults.includes(c.fieldId))
}

function clearActiveSource() {
  activeTableSource.value = null
  selectedTableColumns.value = []
}

function isColumnSelected(field: FieldDefinition): boolean {
  const dynField = buildDynamicField(field)
  return selectedTableColumns.value.some(c => c.fieldId === dynField.fieldId)
}

function toggleTableColumn(field: FieldDefinition) {
  const dynField = buildDynamicField(field)
  const idx = selectedTableColumns.value.findIndex(c => c.fieldId === dynField.fieldId)
  if (idx >= 0) {
    selectedTableColumns.value.splice(idx, 1)
  } else {
    selectedTableColumns.value.push({
      fieldId: dynField.fieldId,
      fieldLabel: dynField.fieldLabel,
      fieldType: dynField.fieldType,
    })
  }
}

function removeTableColumn(fieldId: string) {
  selectedTableColumns.value = selectedTableColumns.value.filter(c => c.fieldId !== fieldId)
}

function emitInsertTable() {
  if (selectedTableColumns.value.length === 0) return
  emit('insert-table', {
    module: selectedModule.value,
    columns: [...selectedTableColumns.value],
    source: activeTableSource.value || undefined,
  })
  selectedTableColumns.value = []
  activeTableSource.value = null
}

const quotationPresets: Record<string, { columns: TemplateDynamicTableColumn[] }> = {
  quotationContainers: {
    columns: [
      { fieldId: 'type', fieldLabel: 'Tipo', fieldType: 'text' },
      { fieldId: 'packages', fieldLabel: 'Bultos', fieldType: 'text' },
      { fieldId: 'grossWeight', fieldLabel: 'Peso Bruto', fieldType: 'text' },
      { fieldId: 'volume', fieldLabel: 'Volumen', fieldType: 'text' },
    ],
  },
  quotationCharges: {
    columns: [
      { fieldId: 'concept', fieldLabel: 'Concepto', fieldType: 'text' },
      { fieldId: 'category', fieldLabel: 'Categoría', fieldType: 'text' },
      { fieldId: 'unitValue', fieldLabel: 'Valor Unit.', fieldType: 'currency' },
      { fieldId: 'tax', fieldLabel: 'IVA', fieldType: 'currency' },
      { fieldId: 'total', fieldLabel: 'Total', fieldType: 'currency' },
      { fieldId: 'currency', fieldLabel: 'Moneda', fieldType: 'text' },
    ],
  },
}

function insertQuotationPreset(source: string) {
  const preset = quotationPresets[source]
  if (!preset) return
  emit('insert-table', {
    module: 'shipments',
    columns: preset.columns,
    source,
  })
}

const PARTY_FIELDS: FieldDefinition[] = [
  { id: 'party_shipper', name: 'shipper', label: 'Shipper / Remitente', labelES: 'Shipper / Remitente', labelEN: 'Shipper', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 900 },
  { id: 'party_consignee', name: 'consignee', label: 'Consignee / Consignatario', labelES: 'Consignee / Consignatario', labelEN: 'Consignee', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 901 },
  { id: 'party_notify', name: 'notify', label: 'Notify Party', labelES: 'Notify Party', labelEN: 'Notify Party', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 902 },
  { id: 'party_pickup', name: 'pickup', label: 'Pickup Location', labelES: 'Pickup Location', labelEN: 'Pickup Location', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 903 },
  { id: 'party_delivery', name: 'delivery', label: 'Delivery Location', labelES: 'Delivery Location', labelEN: 'Delivery Location', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 904 },
  { id: 'party_customer', name: 'customer', label: 'Customer / Cliente', labelES: 'Customer / Cliente', labelEN: 'Customer', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 905 },
  { id: 'party_vendor', name: 'vendor', label: 'Supplier / Proveedor', labelES: 'Supplier / Proveedor', labelEN: 'Supplier / Vendor', type: 'text' as any, module: 'shipments' as any, section: 'parties' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 906 },
]

const STATUS_FIELDS: Record<TemplateModuleSource, FieldDefinition> = {
  shipments: { id: 'ship_status', name: 'status', label: 'Estado del Embarque', labelES: 'Estado del Embarque', labelEN: 'Shipment Status', type: 'text' as any, module: 'shipments' as any, section: 'additionalInfo' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 910 },
  purchaseOrders: { id: 'po_status', name: 'status', label: 'Estado', labelES: 'Estado', labelEN: 'Status', type: 'text' as any, module: 'purchaseOrders' as any, section: 'general' as any, required: false, visible: true, editable: false, sortable: false, filterable: false, searchable: false, isCustom: false, isSystem: true, order: 910 },
}

const moduleFields = computed<FieldDefinition[]>(() => {
  const fields = getModuleFields.value(selectedModule.value) || []
  // El estado debe poder usarse en plantillas aunque la compañía lo tenga oculto
  // en la configuración de campos del módulo.
  const hasVisibleStatus = fields.some(f => f.name === 'status' && f.visible !== false)
  const base = hasVisibleStatus ? fields : [...fields, STATUS_FIELDS[selectedModule.value]]
  if (selectedModule.value === 'shipments') {
    return [...base, ...PARTY_FIELDS]
  }
  return base
})

const filteredFields = computed(() => {
  let fields = moduleFields.value.filter((f) => f.visible !== false)

  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase()
    fields = fields.filter(
      (f) =>
        (f.label || '').toLowerCase().includes(q) ||
        (f.name || '').toLowerCase().includes(q) ||
        (f.section || '').toLowerCase().includes(q)
    )
  }

  return fields.sort((a, b) => (a.order || 0) - (b.order || 0))
})

interface FieldGroup {
  section: string
  fields: FieldDefinition[]
}

const groupedFields = computed<FieldGroup[]>(() => {
  const groups: Record<string, FieldDefinition[]> = {}

  for (const field of filteredFields.value) {
    const section = field.section || 'otros'
    if (!groups[section]) groups[section] = []
    groups[section].push(field)
  }

  return Object.entries(groups)
    .map(([section, fields]) => ({ section, fields }))
    .sort((a, b) => a.section.localeCompare(b.section))
})

function formatSectionLabel(section: string): string {
  const labels: Record<string, string> = {
    references: 'Referencias',
    transportConfig: 'Config. Transporte',
    incoterm: 'Incoterm',
    transportDocs: 'Documentos Transporte',
    carriers: 'Transportistas',
    requestedDates: 'Fechas Solicitadas',
    estimatedTimes: 'Tiempos Estimados',
    actualEvents: 'Eventos Reales',
    travelInfo: 'Info de Viaje',
    lastMile: 'Última Milla',
    additionalInfo: 'Info Adicional',
    tableOnly: 'Campos de Tabla',
    general: 'General',
    dates: 'Fechas',
    financial: 'Financiero',
    logistics: 'Logística',
    commercial: 'Comercial',
    parties: 'Parties / Contactos',
    items: 'Items/Productos',
    otros: 'Otros',
  }
  return labels[section] || section
}

function buildDynamicField(field: FieldDefinition): TemplateDynamicField {
  const isCross = field.isCrossModule && field.sourceModule
  // Para campos cross-module, usar el módulo origen y el nombre original (sin prefijo xm_)
  const actualModule = isCross ? field.sourceModule! : selectedModule.value
  const actualFieldId = isCross
    ? (field.name || field.id).replace(`xm_${field.sourceModule}_`, '')
    : (field.name || field.id)

  return {
    module: actualModule as TemplateModuleSource,
    fieldId: actualFieldId,
    fieldLabel: field.label || field.name,
    section: field.section as string | undefined,
    fieldType: field.type as string | undefined,
  }
}

// Cargar campos al montar
onMounted(async () => {
  const companyId = companyStore.selectedCompanyId
  if (companyId) {
    await loadSystemFieldsConfig(companyId, 'shipments')
    await loadSystemFieldsConfig(companyId, 'purchaseOrders')
  }
})

// Recargar cuando cambie el módulo
watch(selectedModule, async () => {
  const companyId = companyStore.selectedCompanyId
  if (companyId) {
    await loadSystemFieldsConfig(companyId, selectedModule.value)
  }
})
</script>
